import type { Faq as FaqType } from 'libs/http/api/content/content.types';

import type { FeatureGridItem } from 'ui/atoms/FeatureGrid/FeatureGrid';
import type { DeliveryTableRow } from 'ui/organisms/DeliveryTable/DeliveryTable.types';
import type { ScrollRevealItem } from 'ui/organisms/ScrollRevealSection/ScrollRevealSection.types';
import type { DataCases } from 'ui/organisms/UseCases/UseCases';

type Translate = (key: string) => string;

export const getDeliveryRows = (t: Translate): DeliveryTableRow[] => [
  {
    name: t('Platform'),
    tag: t('No integration'),
    desc: t(
      'Search, validate and export from the web application. Built for analysts running checks one company at a time.',
    ),
    actions: [{ label: t('Start free trial'), href: 'https://globaldatabase.com/free-trial' }],
  },
  {
    name: 'API',
    tag: 'REST · JSON',
    desc: t(
      'Single lookup or batch. Validation, registry resolution and the evidence reference come back in one response, priced per result rather than per token.',
    ),
    actions: [{ label: t('Read the docs'), href: 'https://globaldatabase.com/api' }],
  },
  {
    name: t('Bulk data'),
    tag: 'CSV · S3 · SFTP',
    desc: t(
      'Daily, weekly or monthly exports loaded into your own database. Send the whole vendor or seller master and get it back matched. Reseller and derivative rights available.',
    ),
    actions: [{ label: t('Talk to sales'), href: 'https://globaldatabase.com/book-a-demo' }],
  },
  {
    name: t('Browser extension'),
    tag: 'Chrome',
    desc: t(
      'Check a company’s tax registration and registry entry from any website or LinkedIn profile without leaving the page.',
    ),
    actions: [{ label: t('Add to Chrome'), href: 'https://globaldatabase.com/outreach' }],
  },
  {
    name: 'CRM',
    tag: 'Salesforce · HubSpot · Dynamics 365',
    desc: t(
      'Validation on the record your team already works in, matched on registration number rather than company name. Registry fields refresh on the same daily sync, so the account carries filed data instead of typed notes.',
    ),
    actions: [
      { label: 'Salesforce', href: 'https://globaldatabase.com/salesforce-integration' },
      { label: 'HubSpot', href: 'https://globaldatabase.com/hubspot-data-enrichment' },
    ],
  },
  {
    name: 'Claude & ChatGPT',
    tag: t('Connector'),
    desc: t(
      'One-click connectors for both assistants. Ask in plain language which suppliers lost their VAT registration this quarter and get the answer with its source attached.',
    ),
    actions: [
      { label: t('Add to Claude'), href: 'https://globaldatabase.com/mcp' },
      { label: t('Add to ChatGPT'), href: 'https://globaldatabase.com/mcp' },
    ],
  },
  {
    name: 'MCP server',
    tag: t('Endpoint'),
    desc: t(
      'Validation and registry resolution exposed as tools to Claude and any other Model Context Protocol client, on the same key and the same provenance fields.',
    ),
    actions: [{ label: t('View setup'), href: 'https://globaldatabase.com/mcp' }],
  },
  {
    name: 'Regis AI',
    tag: t('In platform · API · Agentic'),
    desc: t(
      'Our own model, in the platform and over the API. Ask a question that spans numbers, entities or borders and it plans which authorities and registers can answer, calls them, and hands back every value with its source, reference and timestamp. It cannot answer from training alone.',
    ),
    actions: [{ label: 'Regis API', href: '/regis-api' }],
  },
];

export const INTEGRATE_WORKFLOW_ITEMS: DataCases[] = [
  {
    icon: 'company-33',
    title: 'API Access',
    description: 'Embed into your workflow for real-time validation at scale',
  },
  {
    icon: 'company-34',
    title: 'Batch Processing',
    description: 'Validate thousands of tax IDs via CSV/XLS uploads',
  },
  {
    icon: 'company-35',
    title: 'Online Platform',
    description: 'Perform manual lookups via an intuitive web interface',
  },
  {
    icon: 'company-36',
    title: 'Browser Extension',
    description: 'Instantly validate tax IDs while browsing websites or databases',
  },
];

export const WHATS_INCLUDED_ITEMS: FeatureGridItem[] = [
  {
    icon: 'i-47',
    title: 'Real-time validation of tax IDs (VAT, EIN, CNPJ, etc.)',
  },
  {
    icon: 'i-20',
    title: 'Cross-match with legal entity name, address, and registration number',
  },
  {
    icon: 'i-81',
    title: 'Source-level registry metadata and status (active/inactive)',
  },
  {
    icon: 'i-45',
    title: 'Risk flags for inconsistencies, shell indicators, or mismatches',
  },
  {
    icon: 'i-0',
    title: 'Optional add-ons: UBO, financials, credit score, ownership hierarchy',
  },
];

export const SET_APART_ITEMS: ScrollRevealItem[] = [
  {
    number: '01',
    title: 'Direct From Government Sources',
    description:
      'Every tax ID is validated against the issuing authority — HMRC, IRS, EU VIES, Receita Federal, and 100+ more. No third-party aggregators, no stale data. You get the official status, exactly as the registry reports it.',
    imageUrl: 'img/mcp/passport.png',
  },
  {
    number: '02',
    title: 'Global, Standardized Access',
    description:
      'A single platform to validate VAT, TIN, EIN, ABN, CNPJ, GSTIN, and more — all from authoritative tax bodies.',
    imageUrl: 'img/mcp/passport.png',
  },
  {
    number: '03',
    title: 'Full Entity Context Beyond the Tax ID',
    description:
      "Validation alone is not enough. We enrich every result with the registered legal name, address, incorporation status, and optional data layers — UBO, financials, credit score, and ownership hierarchy — so you have a complete picture of who you're dealing with.",
    imageUrl: 'img/mcp/passport.png',
  },
  {
    number: '04',
    title: 'Built for Enterprise Workflows',
    description:
      "Integrate via REST API or batch upload. Whether you're screening thousands of suppliers or running real-time onboarding checks, our infrastructure scales with your volume — with SLA-backed uptime and developer-friendly documentation.",
    imageUrl: 'img/mcp/passport.png',
  },
  {
    number: '05',
    title: 'Used by Risk, Finance & Compliance Leaders',
    description:
      'Global enterprises across banking, insurance, procurement, and legal rely on Global Database to meet KYB, AML, and VAT compliance requirements — in markets where mistakes carry regulatory and financial consequences.',
    imageUrl: 'img/mcp/passport.png',
  },
];

export const getFaqData = (t: Translate): FaqType[] => [
  {
    id: 'vies-hmrc-free',
    question: t('VIES and HMRC are free. Why pay for this?'),
    answer: `<p>${t(
      'Because a validity flag is not the answer most teams need. The free services confirm a number is registered; the European Commission is explicit that VIES is a search engine rather than a database, and its own guidance notes the service is periodically unavailable while national databases back up. We validate against the authority, resolve the number to the legal entity in the company register, keep the evidence, and re-check daily.',
    )}</p>`,
    default_opened: true,
  },
  {
    id: 'ein-validation',
    question: t('How does EIN validation actually work?'),
    answer: `<p>${t(
      'There is no open IRS database to query. Validation is a match: the EIN is checked together with the legal name, and the result confirms whether the two agree. Everything a compliance or AP team needs after that — registered address, entity status, incorporation date, officers, group structure — comes from state filings, which we hold and join to the EIN.',
    )}</p>`,
  },
  {
    id: 'scraping',
    question: t('Do you scrape any of this?'),
    answer: `<p>${t(
      'No. Every identifier is checked against the authority that issued it, and every registry field is sourced directly from the government register that holds it. Each field carries its source and the date it was retrieved.',
    )}</p>`,
  },
  {
    id: 'authority-offline',
    question: t('What happens when a tax authority is offline?'),
    answer: `<p>${t(
      'The check returns unavailable — explicitly, not as a rejection. The request is queued and retried on a backoff schedule, and the resolved result is delivered without you polling for it. Treating an outage as an invalid number is one of the most common and most expensive implementation errors.',
    )}</p>`,
  },
  {
    id: 'de-es-company-data',
    question: t('Can you return company data for German and Spanish VAT numbers?'),
    answer: `<p>${t(
      'Yes. Where a member state confirms validity without releasing trader name and address, we take the confirmation from the authority and resolve the entity from the national company register — the Handelsregister and the Registro Mercantil respectively. The number still lands on a named, status-checked company.',
    )}</p>`,
  },
  {
    id: 'audit-evidence',
    question: t('Is this suitable as audit evidence?'),
    answer: `<p>${t(
      'Each check produces a timestamped record containing the number submitted, the requester number, the authority queried, the response, the consultation reference where one is issued, and a snapshot of the registry data at that moment. Whether that satisfies a specific assessment is a matter for your tax advisers, but the record exists and is exportable.',
    )}</p>`,
  },
  {
    id: 'deregistration-speed',
    question: t('How quickly do you detect a deregistration?'),
    answer: `<p>${t(
      'Monitored numbers are re-checked against the issuing authority on a daily cycle, and the change is pushed into your systems rather than waiting for you to ask. Authorities themselves publish with a lag, so the honest answer is: as soon as the register shows it.',
    )}</p>`,
  },
  {
    id: 'identifiers-covered',
    question: t('What identifiers do you cover beyond EIN and VAT?'),
    answer: `<p>${t(
      'National tax and business identifiers including GSTIN, ABN, CNPJ, UEN, UID and organisation numbers, alongside EU and UK VAT registration numbers and US EINs. The full list, with the issuing authority and the register each is joined to, is published in the data dictionary.',
    )}</p>`,
  },
  {
    id: 'redistribute',
    question: t('Can we redistribute the results inside our own product?'),
    answer: `<p>${t(
      'Yes, under licence. Identifiers are open and there is no proprietary key locking you in — you keep the government registration numbers and can move providers without re-mapping your master data. Redistribution terms are agreed commercially.',
    )}</p>`,
  },
  {
    id: 'time-to-go-live',
    question: t('How long does it take to go live?'),
    answer: `<p>${t(
      'The free trial gives you API access the same day. Bulk validation of an existing vendor or seller master is usually a single file exchange. Integration work is measured in days, not quarters.',
    )}</p>`,
  },
];
